import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";

/**
 * Shared frame for the Super Admin, Tenant Admin and Seller consoles.
 * Wide screens: a fixed sidebar. Narrow screens: a top bar with a slide-in menu.
 *
 * Props: testid, overline, title, subtitle (node), nav [{ to, icon, label, end, testid }],
 *        onLogout, logoutTestid,
 *        status ("checking" | "ready" | "unreachable") and onRetry, from useVerifiedSession:
 *        the console's pages are rendered only when the login is confirmed
 */
export default function AdminShell({ testid, overline, title, subtitle, nav, onLogout, logoutTestid, status = "ready", onRetry }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Navigating closes the menu
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#F5F5F5] admin-body" data-testid={testid}>
      <header className="lg:hidden sticky top-0 z-30 flex items-center gap-2 bg-white border-b border-neutral-200 px-2 h-14">
        <button type="button" onClick={() => setOpen(true)} aria-label="Open menu" className="icon-btn" data-testid={`${testid}-menu-btn`}>
          <Menu className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <div className="admin-overline truncate">{overline}</div>
          <div className="admin-heading text-sm font-bold truncate">{title}</div>
        </div>
      </header>

      <div className="lg:flex">
        {open && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setOpen(false)} aria-hidden="true" />}

        <aside
          className={`fixed inset-y-0 left-0 z-50 w-64 max-w-[85vw] bg-white border-r border-neutral-200 flex flex-col transition-transform duration-200
            lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:w-60 lg:flex-shrink-0 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
        >
          <div className="p-5 border-b border-neutral-200 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="admin-overline truncate">{overline}</div>
              <div className="admin-heading text-base font-bold mt-1 break-words">{title}</div>
              {subtitle}
            </div>
            <div className="lg:hidden -mr-2 -mt-2 flex-shrink-0">
              <button type="button" onClick={() => setOpen(false)} aria-label="Close menu" className="icon-btn">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <nav className="flex-1 overflow-y-auto py-2">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-3 text-sm border-l-2 ${isActive ? "border-black bg-neutral-50 font-semibold text-black" : "border-transparent text-neutral-600 hover:bg-neutral-50"}`
                }>
                <n.icon className="h-4 w-4 flex-shrink-0" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <button type="button" onClick={onLogout} data-testid={logoutTestid}
            className="m-4 flex items-center justify-center gap-2 bg-black text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-800">
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </aside>

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
          {status === "ready" && <Outlet />}
          {status === "checking" && <div className="text-sm text-neutral-500" data-testid={`${testid}-checking`}>Loading…</div>}
          {status === "unreachable" && (
            <div className="bg-white border border-neutral-200 p-6 max-w-md" data-testid={`${testid}-unreachable`}>
              <div className="font-semibold">The server could not be reached</div>
              <p className="text-sm text-neutral-600 mt-1">You are still signed in. Check the connection and try again.</p>
              <button type="button" onClick={onRetry} data-testid={`${testid}-retry`}
                className="mt-4 bg-black text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-800 min-h-[44px]">
                Try again
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
